import { eachDayOfInterval, format } from "date-fns"
import { ChevronLeft, ChevronRight, Trash } from "lucide-react"
import { Preset } from "shared-ts/libs/dates/presets"

import { CalendarCell } from "./CalendarCell"
import { PresetButton } from "./PresetButton"
import { TextDateInput } from "./TextDateInput"
import {
  UseIntervalPickerProps,
  useIntervalPicker,
  daysOfWeek,
  getDisplayDays,
} from "../../../../components/composite/TimestampRangePicker"
import { IconButton } from "../Filter/FilterHeaderRow"

interface IntervalPickerProps extends UseIntervalPickerProps {
  presetsToDisplay: Preset[]
  clearable?: boolean
}

export function IntervalPicker({
  presetsToDisplay,
  clearable = true,
  ...useIntervalPickerProps
}: IntervalPickerProps) {
  const {
    monthDisplayed,
    onClickPreviousMonth,
    onClickNextMonth,
    currentDateField,
    displayRange,
    onDateClicked,
    onSelectPreset,
    onDelete,
    startInputProps,
    endInputProps,
    disabledMode,
  } = useIntervalPicker(useIntervalPickerProps)

  return (
    <div className="divide-v2-border-default flex flex-row gap-2 divide-x">
      <div className="flex flex-col gap-2 p-4">
        {presetsToDisplay.map((preset) => (
          <div key={preset.id} className="flex w-28 justify-start">
            <PresetButton
              preset={preset}
              onSelectPreset={onSelectPreset}
              isSelected={!!displayRange && preset.matches(displayRange)}
            />
          </div>
        ))}
      </div>
      <div className="flex flex-col gap-4 p-4">
        <div className="flex flex-row items-center justify-between">
          <div className="flex flex-row items-center gap-1">
            <div className="text-label">Date</div>
          </div>
          {clearable && <IconButton onClick={onDelete} Icon={Trash} />}
        </div>
        <div className="flex flex-row gap-3">
          <TextDateInput {...startInputProps} />
          <TextDateInput {...endInputProps} />
        </div>
        <div className="flex flex-row items-center justify-between">
          <div className="text-title-sm">
            {format(monthDisplayed, "MMMM yyyy")}
          </div>
          <div className="flex flex-row gap-2">
            <IconButton
              Icon={ChevronLeft}
              onMouseDown={(e) => {
                e.preventDefault()
                onClickPreviousMonth()
              }}
              tabIndex={-1}
            />
            <IconButton
              Icon={ChevronRight}
              onMouseDown={(e) => {
                e.preventDefault()
                onClickNextMonth()
              }}
              tabIndex={-1}
            />
          </div>
        </div>
        <div className="flex w-56 flex-wrap">
          {daysOfWeek.map((day) => (
            <div key={day.id} className="text-body h-8 w-8 text-center">
              {day.Dd}
            </div>
          ))}
          {eachDayOfInterval(getDisplayDays(monthDisplayed)).map((day) => (
            <CalendarCell
              key={day.toDateString()}
              date={day}
              range={displayRange}
              displayMonth={monthDisplayed}
              currentlySelecting={currentDateField}
              onDateClicked={onDateClicked}
              disabledMode={disabledMode}
            />
          ))}
        </div>
      </div>
    </div>
  )
}
